if (!customElements.get('article-progress')) {
  customElements.define(
    'article-progress',
    class ArticleProgress extends HTMLElement {
      constructor() {
        super();
        this._raf = null;
        this._onScroll = this._onScroll.bind(this);
        this._onResize = this._onResize.bind(this);
      }

      connectedCallback() {
        this.classList.add('article-progress');
        this.setAttribute('role', 'progressbar');
        this.setAttribute('aria-valuemin', '0');
        this.setAttribute('aria-valuemax', '100');
        this.setAttribute('aria-valuenow', '0');

        this._target = document.querySelector(this.getAttribute('data-target') || '.article-zimply__body');
        if (!this._target) return;

        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize, { passive: true });
        this._update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onResize);
        if (this._raf) cancelAnimationFrame(this._raf);
      }

      _onScroll() {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this._update();
        });
      }

      _onResize() {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this._update();
        });
      }

      _targetRect() {
        const rect = this._target.getBoundingClientRect();
        // On tablet/mobile the body column is `display: contents` so its children
        // can reorder into the parent grid. Such an element generates no box and
        // getBoundingClientRect() returns zeros — derive the span from its children.
        if (rect.height > 0) return rect;
        const kids = this._target.children;
        if (!kids.length) return rect;
        const first = kids[0].getBoundingClientRect();
        const last = kids[kids.length - 1].getBoundingClientRect();
        return { top: first.top, height: last.bottom - first.top };
      }

      _update() {
        if (!this._target) return;
        const rect = this._targetRect();
        const vh = window.innerHeight;
        const total = rect.height - vh * 0.6;
        const scrolled = vh * 0.4 - rect.top;
        let pct = total > 0 ? (scrolled / total) * 100 : 0;
        if (pct < 0) pct = 0;
        if (pct > 100) pct = 100;
        this.style.setProperty('--article-progress', pct.toFixed(2) + '%');
        this.setAttribute('aria-valuenow', Math.round(pct));
      }
    }
  );
}

if (!customElements.get('article-toc')) {
  customElements.define(
    'article-toc',
    class ArticleToc extends HTMLElement {
      constructor() {
        super();
        this._observer = null;
        this._linkMap = new Map();
      }

      connectedCallback() {
        const sourceSelector = this.getAttribute('data-source') || '.article-zimply__body';
        const headingSelector = this.getAttribute('data-heading-selector') || 'h2';
        const minHeadings = parseInt(this.getAttribute('data-min-headings') || '2', 10);
        const source = document.querySelector(sourceSelector);
        if (!source) {
          this._hide();
          return;
        }

        const headings = Array.from(source.querySelectorAll(headingSelector));
        if (headings.length < minHeadings) {
          this._hide();
          return;
        }

        this._buildList(headings);
        this._observeHeadings(headings);
        this._bindClicks();
      }

      disconnectedCallback() {
        if (this._observer) this._observer.disconnect();
      }

      _hide() {
        this.style.display = 'none';
        const col = this.closest('.article-zimply__toc-col');
        if (col) col.style.display = 'none';
        const layout = this.closest('.article-zimply__layout');
        if (layout) layout.classList.remove('has-toc');
        this.dispatchEvent(new CustomEvent('article-toc:empty', { bubbles: true }));
      }

      _slugify(text) {
        return (text || '')
          .toLowerCase()
          .replace(/ä/g, 'ae')
          .replace(/ö/g, 'oe')
          .replace(/ü/g, 'ue')
          .replace(/ß/g, 'ss')
          .replace(/[^a-z0-9\s-]/g, '')
          .trim()
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .substring(0, 80);
      }

      _ensureUniqueId(base, used) {
        let id = base || 'section';
        let n = 2;
        while (used.has(id) || document.getElementById(id)) {
          id = `${base}-${n++}`;
        }
        used.add(id);
        return id;
      }

      _buildList(headings) {
        const list = this.querySelector('[data-toc-list]');
        if (!list) return;

        const used = new Set();
        const rootOl = document.createElement('ol');

        headings.forEach((h) => {
          if (!h.id) {
            h.id = this._ensureUniqueId(this._slugify(h.textContent), used);
          } else {
            used.add(h.id);
          }

          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = `#${h.id}`;
          a.textContent = h.dataset.tocLabel || h.textContent;
          a.dataset.tocFor = h.id;
          li.appendChild(a);
          this._linkMap.set(h.id, a);
          rootOl.appendChild(li);
        });

        list.appendChild(rootOl);
      }

      _observeHeadings(headings) {
        if (!('IntersectionObserver' in window)) return;
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const link = this._linkMap.get(entry.target.id);
              if (!link) return;
              if (entry.isIntersecting) {
                this._linkMap.forEach((l) => l.classList.remove('is-active'));
                link.classList.add('is-active');
              }
            });
          },
          { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
        );
        headings.forEach((h) => this._observer.observe(h));
      }

      _bindClicks() {
        this.addEventListener('click', (e) => {
          const link = e.target.closest('a[href^="#"]');
          if (!link) return;
          const id = link.getAttribute('href').slice(1);
          const target = document.getElementById(id);
          if (!target) return;
          e.preventDefault();
          const headerOffset = parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height') ||
              getComputedStyle(document.documentElement).getPropertyValue('--header-height') ||
              '0'
          );
          const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 16;
          window.scrollTo({ top, behavior: 'smooth' });
          history.replaceState(null, '', `#${id}`);
        });
      }
    }
  );
}
